import type React from 'react'

import { Link } from '@tanstack/react-router'

import { Icons } from '@/components/atoms/Icons/Icons'
import { cn } from '@/lib/utils'

export type MessagingRailEntryKey = 'teams' | 'channel'

export interface MessagingRailEntry {
  key: MessagingRailEntryKey
  label: string
  to: string
  params?: Record<string, string>
  search?: Record<string, unknown>
  active: boolean
  hasUnread: boolean
}

export interface MessagingRailProps {
  entries: MessagingRailEntry[]
}

const entryIcon: Record<MessagingRailEntryKey, (props: { className?: string }) => React.ReactElement> = {
  teams: Icons.diversity3,
  channel: Icons.wavingHand,
}

export const MessagingRail: React.FC<MessagingRailProps> = ({ entries }) => (
  <nav className="flex w-[72px] flex-shrink-0 flex-col items-center gap-6 py-1">
    {/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */}
    {entries.map((entry) => {
      const EntryIcon = entryIcon[entry.key]
      return (
        <Link
          key={entry.key}
          to={entry.to as any}
          params={entry.params as any}
          search={entry.search as any}
          data-testid={`messaging-rail-${entry.key}`}
          aria-current={entry.active ? 'page' : undefined}
          className={cn(
            'relative flex h-12 w-full flex-col items-center justify-center rounded-lg border px-2 py-1',
            entry.active
              ? 'border-teal-200 bg-gray-900 text-teal-200'
              : 'border-transparent text-gray-400 hover:bg-gray-900/40'
          )}>
          <EntryIcon className="size-6" />
          <span className="text-sm select-none">{entry.label}</span>
          {entry.hasUnread && (
            <span
              data-testid={`messaging-rail-${entry.key}-unread`}
              className="absolute top-1 right-1 size-3 rounded-full bg-teal-400"
            />
          )}
        </Link>
      )
    })}
    {/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */}
  </nav>
)
